import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getDealers } from "@insite/client-framework/Services/DealerService";
import siteMessage from "@insite/client-framework/SiteMessage";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import { CountryModel, DealerModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button from "@insite/mobius/Button";
import DynamicDropdown, { OptionObject } from "@insite/mobius/DynamicDropdown";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@ucs/MobiusOverrides/TextField/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import UcsReCaptcha from "@ucs/Components/UcsReCaptcha";
import { ucsGetCountriesAndStates } from "@ucs/Handlers/UcsCustomApis";
import ucsGuestSampleRequest, { SampleAddress, SampleRequestModel } from "@ucs/Handlers/UcsGuestSampleRequest";
import { formatPhoneNumber } from "@ucs/Helpers/Formatting";
import {
    validateAddress1,
    validateCity,
    validateCompanyNameField,
    validateCountry,
    validateEmailField,
    validateFirstNameField,
    validateLastNameField,
    validatePhoneField,
    validatePostalCode,
    validateProjectDescription,
    validateQuantityField,
    validateState,
    validateStateName,
} from "@ucs/Helpers/FormFieldsValidation";
import customLoadDealers from "@ucs/Store/Custom/CustomLoadDealers";
import { getDealersDropdown, getGeoPermission } from "@ucs/Store/CustomSelectors";
import { CustomState } from "@ucs/Store/Reducers";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { ChangeEvent, RefObject, useEffect } from "react";
import { FC, useContext, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productName: string;
    productSku: string;
    productBrand: string;
    isModalOpen: boolean;
    toggleModal: Function;
    extendedStyles?: SampleRequestFormStyles;
    redirectOnSubmit: LinkFieldValue;
}

const mapDispatchToProps = {
    ucsGuestSampleRequest,
    customLoadDealers,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

const mapStateToProps = (state: CustomState) => {
    return {
        isGeoPermissionDenied: getGeoPermission(state),
        dealersDropdown: getDealersDropdown(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SampleRequestFormStyles {
    formGroup?: InjectableCss;
    projectAddressGroup?: InjectableCss;
    qtySection?: InjectableCss;
    qtyMessage?: TypographyProps;
    qtyFieldStyles?: TextFieldPresentationProps;
    sectionalTitle?: TypographyProps;
    textFieldStyles?: TextFieldPresentationProps;
    selectStyles?: SelectPresentationProps;
    messageTextArea?: TextAreaProps;
    buttonSection?: InjectableCss;
    locationSelect?: SelectPresentationProps;
}

export const sampleRequestFormStyles: SampleRequestFormStyles = {
    formGroup: {
        css: css`
            width: 100%;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                ]);
            }}
        `,
    },
    projectAddressGroup: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        gap: 15px;
                    `,
                    css`
                        gap: 15px;
                    `,
                ]);
            }}
        `,
    },
    qtySection: {
        css: css`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                        gap: 16px;
                    `,
                ]);
            }}
        `,
    },
    qtyMessage: {
        size: 14,
        color: UCSColors.gray04,
        css: css`
            line-height: 18px;
        `,
    },
    qtyFieldStyles: {
        labelProps: {
            size: 13,
            weight: 700,
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
            `,
        },
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            inputSelect: css`
                width: 130px;
            `,
            formInputWrapper: css`
                margin-top: 6px !important;
            `,
        },
    },
    sectionalTitle: {
        size: 13,
        weight: 800,
        color: UCSColors.gray03,
        transform: "uppercase",
        variant: "p",
        css: css`
            line-height: 16px;
            letter-spacing: 0.1em;
            margin-top: -4px;
            margin-bottom: 12px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                ]);
            }}
        `,
    },
    textFieldStyles: {
        labelProps: {
            size: 13,
            weight: 700,
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 6px !important;
            `,
        },
    },
    selectStyles: {
        labelProps: {
            size: 13,
            weight: 700,
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 6px !important;
            `,
        },
    },
    messageTextArea: {
        rows: 11,
        css: css`
            resize: vertical;
        `,
    },
    buttonSection: {
        css: css`
            display: flex;
            gap: 10px;
            margin-top: 32px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        & > *:last-child {
                            flex-grow: 1;
                        }
                        padding-bottom: 30px;
                    `,
                ]);
            }}
        `,
    },
};

const Separator = styled.div`
    height: 1px;
    width: 100%;
    margin-top: 32px;
    background-color: ${UCSColors.gray07};
`;

const SampleRequestForm: FC<Props> = ({
    extendedStyles,
    isModalOpen,
    productName,
    productSku,
    productBrand,
    toggleModal,
    ucsGuestSampleRequest,
    customLoadDealers,
    isGeoPermissionDenied,
    dealersDropdown,
    redirectOnSubmit,
    history,
    validateReCaptcha,
}) => {
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();
    const [styles] = useState(() => mergeToNew(sampleRequestFormStyles, extendedStyles));
    const [formLoaded, setFormLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Selected countries
    const [countries, setCountries] = useState<CountryModel[]>();
    const [companySelectedCountry, setCompanySelectedCountry] = useState<CountryModel>();
    const [projectSelectedCountry, setProjectSelectedCountry] = useState<CountryModel>();
    const [shipToSelectedCountry, setShipToSelectedCountry] = useState<CountryModel>();

    // qty
    const [quantity, setQuantity] = useState("1");
    const [quantityError, setQuantityError] = useState("");
    // personal info
    const [firstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState("");
    const [lastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [emailError, setEmailError] = useState("");

    // nearby location
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [locationError, setLocationError] = useState<React.ReactNode>("");

    // company info
    const [companyName, setCompanyName] = useState("");
    const [companyError, setCompanyError] = useState("");
    const [companyAddress, setCompanyAddress] = useState<SampleAddress>({});
    const [companyAddress1Error, setCompanyAddress1Error] = useState("");
    const [companyCountryError, setCompanyCountryError] = useState("");
    const [companyStateError, setCompanyStateError] = useState("");
    const [companyCityError, setCompanyCityError] = useState("");
    const [companyPostalCodeError, setCompanyPostalCodeError] = useState("");
    // project info
    const [message, setMessage] = useState("");
    const [messageError, setMessageError] = useState("");
    const [projectAddress, setProjectAddress] = useState<SampleAddress>({});
    const [projectCountryError, setProjectCountryError] = useState("");
    const [projectStateError, setProjectStateError] = useState("");
    const [projectCityError, setProjectCityError] = useState("");
    // ship to address
    const [shipToAddress, setShipToAddress] = useState<SampleAddress>({});
    const [shipToAddress1Error, setShipToAddress1Error] = useState("");
    const [shipToCountryError, setShipToCountryError] = useState("");
    const [shipToStateError, setShipToStateError] = useState("");
    const [shipToCityError, setShipToCityError] = useState("");
    const [shipToPostalCodeError, setShipToPostalCodeError] = useState("");

    const toasterContext = useContext(ToasterContext);

    React.useEffect(() => {
        setFormLoaded(true);
        if (countries === undefined) {
            ucsGetCountriesAndStates({}).then((value: any) => {
                if (value && value.countries) {
                    setCountries(value.countries);
                }
            });
        }
    }, []);

    React.useEffect(() => {
        isModalOpen && setFormLoaded(true);
    }, [isModalOpen]);

    React.useEffect(() => {
        if (formLoaded && typeof quantity !== "undefined") {
            validateQuantity();
        }
    }, [quantity]);

    // personal info
    React.useEffect(() => {
        if (formLoaded && typeof firstName !== "undefined") {
            if (firstName!.length === 0 || firstName!.length >= 2) {
                validateFirstName();
            }
        }
    }, [firstName]);

    React.useEffect(() => {
        if (formLoaded && typeof lastName !== "undefined") {
            if (lastName!.length === 0 || lastName!.length >= 2) {
                validateLastName();
            }
        }
    }, [lastName]);

    React.useEffect(() => {
        if (formLoaded && typeof emailAddress !== "undefined") {
            if (emailAddress!.length === 0 || emailAddress!.length > 4) {
                validateEmail();
            }
        }
    }, [emailAddress]);

    // company info
    React.useEffect(() => {
        if (formLoaded && typeof companyName !== "undefined") {
            if (companyName!.length === 0 || companyName!.length >= 2) {
                validateCompany();
            }
        }
    }, [companyName]);

    React.useEffect(() => {
        if (formLoaded && typeof companyAddress.addressLine1 !== "undefined") {
            validateCompanyAddress1();
        }
    }, [companyAddress.addressLine1]);

    React.useEffect(() => {
        if (formLoaded && typeof companyAddress.countryId !== "undefined") {
            validateCompanyCountry();
            companyAddress.postalCode && validateCompanyPostalCode();
        }
    }, [companyAddress.countryId]);

    React.useEffect(() => {
        if (formLoaded && typeof companyAddress.stateName !== "undefined") {
            validateCompanyStateName();
        }
    }, [companyAddress.stateName]);

    React.useEffect(() => {
        if (formLoaded && typeof companyAddress.stateId !== "undefined") {
            validateCompanyState();
        }
    }, [companyAddress.stateId]);

    React.useEffect(() => {
        if (formLoaded && typeof companyAddress.city !== "undefined") {
            validateCompanyCity();
        }
    }, [companyAddress.city]);

    React.useEffect(() => {
        if (formLoaded && typeof companyAddress.postalCode !== "undefined") {
            validateCompanyPostalCode() &&
                setCompanyAddress({ ...companyAddress, postalCode: companyAddress.postalCode.toUpperCase() });
        }
    }, [companyAddress.postalCode]);

    // Project info
    React.useEffect(() => {
        if (formLoaded && typeof message !== "undefined") {
            if (message!.length === 0 || message!.length <= 10) {
                validateMessage();
            }
        }
    }, [message]);

    React.useEffect(() => {
        if (formLoaded && typeof projectAddress.countryId !== "undefined") {
            validateProjectCountry();
        }
    }, [projectAddress.countryId]);

    React.useEffect(() => {
        if (formLoaded && typeof projectAddress.stateId !== "undefined") {
            validateProjectState();
        }
    }, [projectAddress.stateId]);

    React.useEffect(() => {
        if (formLoaded && typeof projectAddress.stateName !== "undefined") {
            validateProjectStateName();
        }
    }, [projectAddress.stateName]);

    React.useEffect(() => {
        if (formLoaded && typeof projectAddress.city !== "undefined") {
            validateProjectCity();
        }
    }, [projectAddress.city]);

    // ship to
    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.addressLine1 !== "undefined") {
            validateShipToAddress1();
        }
    }, [shipToAddress.addressLine1]);

    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.countryId !== "undefined") {
            validateShipToCountry();
            shipToAddress.postalCode && validateShipToPostalCode();
        }
    }, [shipToAddress.countryId]);

    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.stateId !== "undefined") {
            validateShipToState();
        }
    }, [shipToAddress.stateId]);

    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.stateName !== "undefined") {
            validateShipToStateName();
        }
    }, [shipToAddress.stateName]);

    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.city !== "undefined") {
            validateShipToCity();
        }
    }, [shipToAddress.city]);

    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.postalCode !== "undefined") {
            validateShipToPostalCode() &&
                setShipToAddress({ ...shipToAddress, postalCode: shipToAddress.postalCode.toUpperCase() });
        }
    }, [shipToAddress.postalCode]);

    // VALIDATION
    const validateQuantity = () => {
        const errorMessage = validateQuantityField(quantity);
        setQuantityError(errorMessage);
        return !errorMessage;
    };

    const validateFirstName = () => {
        const errorMessage = validateFirstNameField(firstName);
        setFirstNameError(errorMessage);
        return !errorMessage;
    };

    const validateLastName = () => {
        const errorMessage = validateLastNameField(lastName);
        setLastNameError(errorMessage);
        return !errorMessage;
    };

    const validatePhone = () => {
        const phoneFormatted: string = formatPhoneNumber(phoneNumber);
        setPhoneNumber(phoneFormatted);
        const errorMessage = validatePhoneField(phoneFormatted);
        setPhoneError(errorMessage);
        return !errorMessage;
    };

    const validateEmail = () => {
        const errorMessage = validateEmailField(emailAddress);
        setEmailError(errorMessage);
        return !errorMessage;
    };

    // validation - company
    const validateCompany = () => {
        const errorMessage = validateCompanyNameField(companyName);
        setCompanyError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyAddress1 = () => {
        const errorMessage = validateAddress1(companyAddress.addressLine1);
        setCompanyAddress1Error(errorMessage);
        return !errorMessage;
    };

    const validateCompanyCountry = () => {
        const errorMessage = validateCountry(companyAddress.countryId);
        setCompanyCountryError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyState = () => {
        const errorMessage = validateState(companyAddress.stateId);
        setCompanyStateError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyStateName = () => {
        const errorMessage = validateStateName(companyAddress.stateName);
        setCompanyStateError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyCity = () => {
        const errorMessage = validateCity(companyAddress.city);
        setCompanyCityError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyPostalCode = () => {
        const errorMessage = companySelectedCountry
            ? validatePostalCode(companyAddress.postalCode, companySelectedCountry.abbreviation)
            : validatePostalCode(companyAddress.postalCode);
        setCompanyPostalCodeError(errorMessage);
        return !errorMessage;
    };

    // validation - project
    const validateMessage = () => {
        const errorMessage = validateProjectDescription(message);
        setMessageError(errorMessage);
        return !errorMessage;
    };

    const validateProjectCountry = () => {
        const errorMessage = validateCountry(projectAddress.countryId);
        setProjectCountryError(errorMessage);
        return !errorMessage;
    };

    const validateProjectState = () => {
        const errorMessage = validateState(projectAddress.stateId);
        setProjectStateError(errorMessage);
        return !errorMessage;
    };

    const validateProjectStateName = () => {
        const errorMessage = validateStateName(projectAddress.stateName);
        setProjectStateError(errorMessage);
        return !errorMessage;
    };

    const validateProjectCity = () => {
        const errorMessage = validateCity(projectAddress.city);
        setProjectCityError(errorMessage);
        return !errorMessage;
    };

    // validation - ship to
    const validateShipToAddress1 = () => {
        const errorMessage = validateAddress1(shipToAddress.addressLine1);
        setShipToAddress1Error(errorMessage);
        return !errorMessage;
    };

    const validateShipToCountry = () => {
        const errorMessage = validateCountry(shipToAddress.countryId);
        setShipToCountryError(errorMessage);
        return !errorMessage;
    };

    const validateShipToState = () => {
        const errorMessage = validateState(shipToAddress.stateId);
        setShipToStateError(errorMessage);
        return !errorMessage;
    };

    const validateShipToStateName = () => {
        const errorMessage = validateStateName(shipToAddress.stateName);
        setShipToStateError(errorMessage);
        return !errorMessage;
    };

    const validateShipToCity = () => {
        const errorMessage = validateCity(shipToAddress.city);
        setShipToCityError(errorMessage);
        return !errorMessage;
    };

    const validateShipToPostalCode = () => {
        const errorMessage = shipToSelectedCountry
            ? validatePostalCode(shipToAddress.postalCode, shipToSelectedCountry.abbreviation)
            : validatePostalCode(shipToAddress.postalCode);
        setShipToPostalCodeError(errorMessage);
        return !errorMessage;
    };
    const validateLocation = () => {
        const errorMessage = !selectedLocation ? translate("Please select a nearest location.") : "";
        setLocationError(errorMessage);
        return !errorMessage;
    };
    // EVENT handlers
    const companyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCompanyName(event.target.value);
    };

    const firstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(event.target.value);
    };

    const lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(event.target.value);
    };

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailAddress(event.target.value);
    };

    const emailBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validateEmail();
    };

    const phoneChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneNumber(event.target.value);
    };

    const phoneBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validatePhone();
    };

    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setQuantity(event.target.value);
    };

    const companyAddress1ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setCompanyAddress({ ...companyAddress, addressLine1: event.target.value });
    };

    const companyAddress2ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setCompanyAddress({ ...companyAddress, addressLine2: event.target.value });
    };

    const companyCountyChangeHandler: SelectProps["onChange"] = event => {
        const country = countries!.find(c => c.id === event.target.value);
        setCompanySelectedCountry(country);
        setCompanyAddress({ ...companyAddress, countryId: country?.id, stateId: "" });
    };

    const companyStateNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setCompanyAddress({ ...companyAddress, stateName: event.target.value });
    };

    const companyStateChangeHandler: SelectProps["onChange"] = event => {
        const state = companySelectedCountry?.states?.find(s => s.id === event.target.value);
        setCompanyAddress({ ...companyAddress, stateId: state?.id });
    };

    const companyCityChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setCompanyAddress({ ...companyAddress, city: event.target.value });
    };

    const companyPostalCodeChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setCompanyAddress({ ...companyAddress, postalCode: event.target.value });
    };

    const projectCountyChangeHandler: SelectProps["onChange"] = event => {
        const country = countries!.find(c => c.id === event.target.value);
        setProjectSelectedCountry(country);
        setProjectAddress({ ...projectAddress, countryId: country?.id, stateId: "" });
    };

    const projectStateNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setProjectAddress({ ...projectAddress, stateName: event.target.value });
    };

    const projectStateChangeHandler: SelectProps["onChange"] = event => {
        const state = projectSelectedCountry?.states?.find(s => s.id === event.target.value);
        setProjectAddress({ ...projectAddress, stateId: state?.id });
    };

    const projectCityChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setProjectAddress({ ...projectAddress, city: event.target.value });
    };

    const shipToAddress1ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setShipToAddress({ ...shipToAddress, addressLine1: event.target.value });
    };

    const shipToAddress2ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setShipToAddress({ ...shipToAddress, addressLine2: event.target.value });
    };

    const shipToCountyChangeHandler: SelectProps["onChange"] = event => {
        const country = countries!.find(c => c.id === event.target.value);
        setShipToSelectedCountry(country);
        setShipToAddress({ ...shipToAddress, countryId: country?.id, stateId: "" });
    };

    const shipToStateNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setShipToAddress({ ...shipToAddress, stateName: event.target.value });
    };

    const shipToStateChangeHandler: SelectProps["onChange"] = event => {
        const state = shipToSelectedCountry?.states?.find(s => s.id === event.target.value);
        setShipToAddress({ ...shipToAddress, stateId: state?.id });
    };

    const shipToCityChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setShipToAddress({ ...shipToAddress, city: event.target.value });
    };

    const shipToPostalCodeChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setShipToAddress({ ...shipToAddress, postalCode: event.target.value });
    };

    const qtyKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    useEffect(() => {
        customLoadDealers({} as any);
        const locationDropdownData = dealersDropdown.dealers || [];
        const locationOptions = locationDropdownData.map((item: DealerModel) => ({
            optionValue: `${item.name}, ${item.state}`,
            optionText: `${item.name}, ${item.state}`,
        }));
        setOptions(locationOptions);
    }, [isGeoPermissionDenied, dealersDropdown]);

    useEffect(() => {
        const updateSelectedLocationFromCookie = () => {
            if (isGeoPermissionDenied === "No") {
                setSelectedLocation(getCookie("SelectedLocation") || "");
            } else {
                setSelectedLocation("");
            }
        };

        updateSelectedLocationFromCookie();

        // Listen for storage events (cross-tab)
        const onStorage = (event: StorageEvent) => {
            if (event.key === "SelectedLocation") {
                updateSelectedLocationFromCookie();
            }
        };
        window.addEventListener("storage", onStorage);

        // Poll for same-tab cookie changes
        let lastCookie = getCookie("SelectedLocation");
        const interval = setInterval(() => {
            const currentCookie = getCookie("SelectedLocation");
            if (currentCookie !== lastCookie) {
                lastCookie = currentCookie;
                updateSelectedLocationFromCookie();
            }
        }, 1000);

        return () => {
            window.removeEventListener("storage", onStorage);
            clearInterval(interval);
        };
    }, [isGeoPermissionDenied]);


    const getSelectedLocation=()=>{      
        if (isGeoPermissionDenied === "No") {
            setSelectedLocation(getCookie("SelectedLocation") || "");
        } else {
            setSelectedLocation("");
        }
    }

    const listChangeHandler = (value?: string) => {
        setSelectedLocation(value || "");
        const errorMessage = !value ? translate("Please select a nearest location.") : "";
        setLocationError(errorMessage);
    };

    const populateModel = () => {
        const model: SampleRequestModel = {
            firstName,
            lastName,
            companyName,
            phoneNumber,
            emailAddress,
            message,
            productName,
            productSku,
            productBrand,
            projectAddress,
            quantity: +quantity,
            billingAddress: companyAddress,
            shippingAddress: shipToAddress,
            location: selectedLocation || "",
            emailTo: getCookie("SalesEmailCookie") || "info@ucfp.com",
            topic: (siteMessage("Request_Sample_Topic") as string) || "Guest sample request",
        };
        return model;
    };

    const clearForm = () => {
        setFormLoaded(false);
        setFirstName("");
        setFirstNameError("");
        setLastName("");
        setLastNameError("");
        setPhoneNumber("");
        setPhoneError("");
        setEmailAddress("");
        setEmailError("");
        setCompanyName("");
        getSelectedLocation();
        setCompanyError("");
        setCompanyAddress({});
        setCompanyAddress1Error("");
        setCompanyCountryError("");
        setCompanyStateError("");
        setCompanyCityError("");
        setCompanyPostalCodeError("");
        setMessage("");
        setProjectAddress({});
        setProjectCountryError("");
        setProjectStateError("");
        setProjectCityError("");
        setShipToAddress({});
        setShipToAddress1Error("");
        setShipToCountryError("");
        setShipToStateError("");
        setShipToCityError("");
        setShipToPostalCodeError("");
        setLocationError("");
    };

    const validateForm = async () => {
        const validationResult: boolean[] = [
            validateCompany(),
            validateFirstName(),
            validateLastName(),
            validatePhone(),
            validateEmail(),
            validateLocation(),
            validateCompanyAddress1(),
            validateCompanyCountry(),
            companySelectedCountry && companySelectedCountry.states && companySelectedCountry.states.length > 0
                ? validateCompanyState()
                : validateCompanyStateName(),
            validateCompanyCity(),
            validateCompanyPostalCode(),
            validateMessage(),
            validateProjectCountry(),
            projectSelectedCountry && projectSelectedCountry.states && projectSelectedCountry.states.length > 0
                ? validateProjectState()
                : validateProjectStateName(),
            validateProjectCity(),
            validateShipToAddress1(),
            validateShipToCountry(),
            shipToSelectedCountry && shipToSelectedCountry.states && shipToSelectedCountry.states.length > 0
                ? validateShipToState()
                : validateShipToStateName(),
            validateShipToCity(),
            validateShipToPostalCode(),
        ];

        if (!validationResult.includes(false)) {
            const isReCaptchaValid = await validateReCaptcha({ location: "SampleRequest", recaptchaRef });
            if (!isReCaptchaValid) return;
            setIsSubmitting(true);
            const sampleRequestModel = populateModel();
            ucsGuestSampleRequest({
                sampleRequestModel,
                onSuccess: () => {
                    setIsSubmitting(false);
                    if (redirectOnSubmit.value) {
                        history.push(redirectOnSubmit.value);
                    }
                    else {
                        toasterContext.addToast({
                            body: siteMessage("Request_Sample_Success"),
                            messageType: "success",
                        });
                        toggleModal();
                    }
                    clearForm();
                },
                onComplete() {
                    // "this" is targeting the object being created, not the parent SFC
                     
                    this.onSuccess?.();
                },
            });
        } else {
            toasterContext.addToast({
                body: siteMessage("Request_Sample_ValidationError"),
                messageType: "danger",
            });
        }
    };

    return (
        <form id="sampleRequestForm" noValidate date-test-selector="sampleRequestForm">
            <StyledWrapper {...styles.qtySection}>
                <TextField
                    {...styles.qtyFieldStyles}
                    label={translate("Quantity")}
                    value={quantity}
                    error={quantityError}
                    type="number"
                    onKeyPress={qtyKeyPressHandler}
                    onChange={qtyChangeHandler}
                    {...styles.qtyFieldStyles}
                />
                <Typography {...styles.qtyMessage}>{siteMessage("Request_Sample_QtyMessage")}</Typography>
            </StyledWrapper>

            <Separator />
            <Typography {...styles.sectionalTitle}>{translate("Personal Info")}</Typography>

            <StyledWrapper {...styles.formGroup}>
                <TextField
                    {...styles.textFieldStyles}
                    label="First Name"
                    required={true}
                    placeholder=""
                    value={firstName || ""}
                    error={firstNameError}
                    onChange={firstNameChangeHandler}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />

                <TextField
                    {...styles.textFieldStyles}
                    label="Last name"
                    required={true}
                    placeholder=""
                    value={lastName || ""}
                    error={lastNameError}
                    onChange={lastNameChangeHandler}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.formGroup}>
                <TextField
                    {...styles.textFieldStyles}
                    label="Phone number"
                    required={true}
                    placeholder="_ _ _ - _ _ _ - _ _ _ _"
                    value={phoneNumber}
                    onChange={phoneChangeHandler}
                    onBlur={phoneBlurHandler}
                    error={phoneError}
                    type="tel"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />

                <TextField
                    {...styles.textFieldStyles}
                    type="email"
                    label="Email address"
                    required={true}
                    placeholder=""
                    value={emailAddress}
                    onChange={emailChangeHandler}
                    onBlur={emailBlurHandler}
                    error={emailError}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
            </StyledWrapper>

            <StyledWrapper {...styles.formGroup}>
                <Select
                    {...styles.locationSelect}
                    label={translate("Nearest Location")}
                    required={true}
                    value={selectedLocation || ""}
                    error={locationError}
                    onChange={event => listChangeHandler(event.target.value)}
                    data-test-selector="nearby_location_dropdown"
                >
                    <option value="">{translate("Select Nearest Location")}</option>
                    {options.map(option => (
                        <option key={option.optionValue} value={option.optionValue}>
                            {option.optionText}
                        </option>
                    ))}
                </Select>
            </StyledWrapper>

            <Separator />
            <Typography {...styles.sectionalTitle}>{translate("Company Info")}</Typography>

            <TextField
                {...styles.textFieldStyles}
                label="Company"
                required={true}
                placeholder=""
                value={companyName}
                error={companyError}
                onChange={companyChangeHandler}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
            />

            <TextField
                {...styles.textFieldStyles}
                name="address1"
                label={translate("Company Address - Line 1")}
                value={companyAddress.addressLine1}
                required={true}
                maxLength={60}
                onChange={companyAddress1ChangeHandler}
                error={companyAddress1Error}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
            />
            <TextField
                {...styles.textFieldStyles}
                label={translate("Company Address - Line 2")}
                value={companyAddress.addressLine2}
                required={false}
                maxLength={60}
                onChange={companyAddress2ChangeHandler}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
            />

            <StyledWrapper {...styles.formGroup}>
                <Select
                    {...styles.selectStyles}
                    label={translate("Country")}
                    value={companyAddress.countryId}
                    disabled={!countries || countries.length === 0}
                    required={true}
                    onChange={companyCountyChangeHandler}
                    error={companyCountryError}
                >
                    <option value="">{translate("Select Country")}</option>
                    {countries &&
                        countries.map(co => (
                            <option key={co.id} value={co.id}>
                                {co.name}
                            </option>
                        ))}
                </Select>

                {!companySelectedCountry || companySelectedCountry.states!.length > 0 ? (
                    <Select
                        {...styles.selectStyles}
                        label={translate("State / Province")}
                        value={companyAddress.stateId}
                        required={true}
                        onChange={companyStateChangeHandler}
                        error={companyStateError}
                        disabled={!companySelectedCountry}
                    >
                        <option value="">{translate("Select State / Province")}</option>
                        {companySelectedCountry?.states?.map(st => (
                            <option key={st.id} value={st.id}>
                                {st.name}
                            </option>
                        ))}
                    </Select>
                ) : (
                    <TextField
                        {...styles.textFieldStyles}
                        label={translate("State / Province")}
                        value={companyAddress.stateName || ""}
                        required={true}
                        minLength={2}
                        error={companyStateError}
                        onChange={companyStateNameChangeHandler}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                    />
                )}
            </StyledWrapper>

            <StyledWrapper {...styles.formGroup}>
                <TextField
                    {...styles.textFieldStyles}
                    label={translate("City")}
                    value={companyAddress.city}
                    required={true}
                    maxLength={60}
                    onChange={companyCityChangeHandler}
                    error={companyCityError}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
                <TextField
                    {...styles.textFieldStyles}
                    label={translate("Zip / Postal Code")}
                    value={companyAddress.postalCode}
                    required={true}
                    onChange={companyPostalCodeChangeHandler}
                    error={companyPostalCodeError}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
            </StyledWrapper>

            <Separator />
            <Typography {...styles.sectionalTitle}>{translate("Project Info")}</Typography>

            <StyledWrapper {...styles.formGroup}>
                <TextArea
                    {...styles.messageTextArea}
                    label={translate("Project short description")}
                    required={true}
                    minLength={10}
                    error={messageError}
                    value={message}
                    onChange={messageChangeHandler}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
                <StyledWrapper {...styles.projectAddressGroup}>
                    <Select
                        {...styles.selectStyles}
                        label={translate("Country")}
                        value={projectAddress.countryId}
                        disabled={!countries || countries.length === 0}
                        required={true}
                        onChange={projectCountyChangeHandler}
                        error={projectCountryError}
                    >
                        <option value="">{translate("Select Country")}</option>
                        {countries &&
                            countries.map(co => (
                                <option key={co.id} value={co.id}>
                                    {co.name}
                                </option>
                            ))}
                    </Select>
                    {!projectSelectedCountry || projectSelectedCountry.states!.length > 0 ? (
                        <Select
                            {...styles.selectStyles}
                            label={translate("State / Province")}
                            value={projectAddress.stateId}
                            required={true}
                            onChange={projectStateChangeHandler}
                            error={projectStateError}
                            disabled={!projectSelectedCountry}
                        >
                            <option value="">{translate("Select State / Province")}</option>
                            {projectSelectedCountry?.states?.map(st => (
                                <option key={st.id} value={st.id}>
                                    {st.name}
                                </option>
                            ))}
                        </Select>
                    ) : (
                        <TextField
                            {...styles.textFieldStyles}
                            label={translate("State / Province")}
                            value={projectAddress.stateName || ""}
                            required={true}
                            minLength={2}
                            error={projectStateError}
                            onChange={projectStateNameChangeHandler}
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                        />
                    )}

                    <TextField
                        {...styles.textFieldStyles}
                        label={translate("City")}
                        value={projectAddress.city}
                        required={true}
                        maxLength={60}
                        onChange={projectCityChangeHandler}
                        error={projectCityError}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                    />
                </StyledWrapper>
            </StyledWrapper>

            <Separator />
            <Typography {...styles.sectionalTitle}>{translate("Ship to address")}</Typography>

            <TextField
                {...styles.textFieldStyles}
                name="address1"
                label={translate("Address - Line 1")}
                value={shipToAddress.addressLine1}
                required={true}
                maxLength={60}
                onChange={shipToAddress1ChangeHandler}
                error={shipToAddress1Error}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
            />
            <TextField
                {...styles.textFieldStyles}
                label={translate("Address - Line 2")}
                value={shipToAddress.addressLine2}
                required={false}
                maxLength={60}
                onChange={shipToAddress2ChangeHandler}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
            />

            <StyledWrapper {...styles.formGroup}>
                <Select
                    {...styles.selectStyles}
                    label={translate("Country")}
                    value={shipToAddress.countryId}
                    disabled={!countries || countries.length === 0}
                    required={true}
                    onChange={shipToCountyChangeHandler}
                    error={shipToCountryError}
                >
                    <option value="">{translate("Select Country")}</option>
                    {countries &&
                        countries.map(co => (
                            <option key={co.id} value={co.id}>
                                {co.name}
                            </option>
                        ))}
                </Select>

                {!shipToSelectedCountry || shipToSelectedCountry.states!.length > 0 ? (
                    <Select
                        {...styles.selectStyles}
                        label={translate("State / Province")}
                        value={shipToAddress.stateId}
                        required={true}
                        onChange={shipToStateChangeHandler}
                        error={shipToStateError}
                        disabled={!shipToSelectedCountry}
                    >
                        <option value="">{translate("Select State / Province")}</option>
                        {shipToSelectedCountry?.states?.map(st => (
                            <option key={st.id} value={st.id}>
                                {st.name}
                            </option>
                        ))}
                    </Select>
                ) : (
                    <TextField
                        {...styles.textFieldStyles}
                        label={translate("State / Province")}
                        value={shipToAddress.stateName || ""}
                        required={true}
                        minLength={2}
                        error={shipToStateError}
                        onChange={shipToStateNameChangeHandler}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                    />
                )}
            </StyledWrapper>

            <StyledWrapper {...styles.formGroup}>
                <TextField
                    {...styles.textFieldStyles}
                    label={translate("City")}
                    value={shipToAddress.city}
                    required={true}
                    maxLength={60}
                    onChange={shipToCityChangeHandler}
                    error={shipToCityError}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
                <TextField
                    {...styles.textFieldStyles}
                    label={translate("Zip / Postal Code")}
                    value={shipToAddress.postalCode}
                    required={true}
                    onChange={shipToPostalCodeChangeHandler}
                    error={shipToPostalCodeError}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
            </StyledWrapper>

            <UcsReCaptcha recaptchaRef={recaptchaRef} location="SampleRequest" />
            <StyledWrapper {...styles.buttonSection}>
                <Button
                    onClick={e => {
                        e.preventDefault();
                        validateForm();
                    }}
                    disabled={isSubmitting}
                >
                    {translate("Send sample request")}
                </Button>
                <Button
                    variant="secondary"
                    onClick={e => {
                        e.preventDefault();
                        toggleModal();
                        clearForm();
                    }}
                >
                    {translate("Cancel")}
                </Button>
            </StyledWrapper>
        </form>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(SampleRequestForm));
